import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnswerPhoto } from '../../models';
import { ChatStrings } from '../../i18n';
import { ChevronIcon, CloseIcon } from '../Icons';

interface PhotoViewerProps {
   photos: AnswerPhoto[];
   start: number;
   strings: ChatStrings;
   /** Called with the photo seen last. */
   onClose: (last: number) => void;
}

/** A swipe shorter than this is a tap. */
const SWIPE_PX = 40;

/**
 * One photo as large as the screen allows, over a dark veil, with its place's name and caption under it: previous and
 * next (also the arrow keys, mirrored right-to-left, and a swipe) when there are more, and a close button, Escape or a
 * tap beside the photo to leave. A modal dialog, so the focus stays inside until it closes.
 */
const PhotoViewer: React.FC<PhotoViewerProps> = ({ photos, start, strings, onClose }) => {
   const dialog = useRef<HTMLDialogElement>(null);
   const [index, setIndex] = useState(Math.max(0, Math.min(start, photos.length - 1)));
   const swipeFrom = useRef<number | null>(null);
   const photo = photos[index];
   const many = photos.length > 1;

   useLayoutEffect(() => {
      const element = dialog.current;
      if (!element) return;
      // Browsers without a modal dialog still show it, over the page
      if (typeof element.showModal === 'function') {
         if (!element.open) element.showModal();
      } else {
         element.setAttribute('open', '');
      }
      return () => {
         if (element.open && typeof element.close === 'function') element.close();
      };
   }, []);

   // The photos either side load ahead, so moving on doesn't wait
   useEffect(() => {
      for (const near of [photos[index - 1], photos[index + 1]]) {
         if (near) new Image().src = near.url;
      }
   }, [index, photos]);

   const step = (by: number) => setIndex((current) => Math.max(0, Math.min(current + by, photos.length - 1)));
   const close = () => onClose(index);

   const onKeyDown = (event: React.KeyboardEvent) => {
      if (!many || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      const rtl = dialog.current ? getComputedStyle(dialog.current).direction === 'rtl' : false;
      const onward = event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
      event.preventDefault();
      step(onward ? 1 : -1);
   };

   const onPointerUp = (event: React.PointerEvent) => {
      const from = swipeFrom.current;
      swipeFrom.current = null;
      if (from === null || !many) return;
      const moved = event.clientX - from;
      if (Math.abs(moved) < SWIPE_PX) return;
      const rtl = dialog.current ? getComputedStyle(dialog.current).direction === 'rtl' : false;
      // Swiping towards the start brings the next photo in
      step((moved < 0) !== rtl ? 1 : -1);
   };

   return (
      <dialog
         ref={dialog}
         className="cx-viewer"
         aria-label={photo.title}
         onCancel={(event) => {
            event.preventDefault();
            close();
         }}
         onKeyDown={onKeyDown}
         onClick={(event) => {
            if (event.target === event.currentTarget) close();
         }}
      >
         <button type="button" className="cx-icon-btn cx-viewer__close" onClick={close} aria-label={strings.closePhoto} autoFocus>
            <CloseIcon />
         </button>

         <div
            className="cx-viewer__frame"
            onPointerDown={(event) => {
               swipeFrom.current = event.clientX;
            }}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
               swipeFrom.current = null;
            }}
            onClick={(event) => {
               if (event.target === event.currentTarget) close();
            }}
         >
            <img key={photo.url} className="cx-viewer__photo" src={photo.url} alt={photo.alt} decoding="async" draggable={false} />
         </div>

         <div className="cx-viewer__bar">
            <p className="cx-viewer__caption">
               <span className="cx-viewer__title" dir="auto">
                  {photo.title}
               </span>
               {photo.caption && (
                  <span className="cx-viewer__note" dir="auto">
                     {photo.caption}
                  </span>
               )}
            </p>
            {many && (
               <div className="cx-viewer__nav">
                  <button type="button" className="cx-icon-btn" onClick={() => step(-1)} disabled={index === 0} aria-label={strings.previousPhoto}>
                     <ChevronIcon className="cx-chevron cx-chevron--back" />
                  </button>
                  <span className="cx-viewer__position" aria-live="polite">
                     <span className="cx-sr-only">{strings.photoSlide(index + 1, photos.length, photo.title)}</span>
                     <span aria-hidden="true">
                        {index + 1} / {photos.length}
                     </span>
                  </span>
                  <button
                     type="button"
                     className="cx-icon-btn"
                     onClick={() => step(1)}
                     disabled={index === photos.length - 1}
                     aria-label={strings.nextPhoto}
                  >
                     <ChevronIcon className="cx-chevron" />
                  </button>
               </div>
            )}
         </div>
      </dialog>
   );
};

export default PhotoViewer;
